import { useMediaQuery, useTheme } from '@mui/material';
import {
  Activity, ArrowRight, Bug, Building2, Bus, Car, ChevronDown, Droplets,
  Flame, Footprints, GraduationCap, Home, Landmark, Leaf, LayoutDashboard,
  Layers, MapPin, Mountain, PieChart as PieChartIcon, ShoppingCart, Shield,
  Stethoscope, Thermometer, Trees, Utensils, Volume2, Waves, Wind,
  Zap,
} from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip as ReTooltip } from 'recharts';

import { COLORS, isHiddenSubscore } from './constants';
import { ShimmerBlock, ShimmerLine, ShimmerValue } from './LoadingAnimations';
import { NegotiationStrategyDialog } from './NegotiationStrategyDialog';
import { ScrollHintList } from './ScrollOverflowHint';
import { Tip } from './Tip';

import { Dialog } from '@/components/common/Dialog';
import { Switch } from '@/components/common/Switch';
import { CloseButton, LegendItem } from '@/components/ds';
import { useDashboardLayout } from '@/context/DashboardLayoutProvider';
import { computeTopConcernGroups, type IssueItem } from '@/hooks/issues/useNegotiationLeverage';
import { VALUE_FONT_SMALL } from '@/theme/consts';
import { NHDFindings } from '@/types/NHD';

export type { IssueItem };

export interface SubscoreStory {
  question: string;
  score: number;
  narrative: string;
  strengths: string[];
  gaps: string[];
}

export interface WorstPillar {
  label: string;
  score: number;
  grade: string;
  topIssues: { title: string; urgency: string; cost?: string }[];
}

export interface SubscoreDetailCardProps {
  stories: Record<string, SubscoreStory>;
  isLoading?: boolean;
  hasData?: boolean;
  error?: boolean;
  worstPillar?: WorstPillar;
  issueList?: IssueItem[];
  /** Raw (pre-dedup) issue count + per-urgency breakdown for display — matches HomeDNA/chat. */
  totalIssueCount?: number;
  rawUrgencyCounts?: Record<string, number>;
  baseScore?: number;
  mode?: 'condition' | 'context';
  /** 27": the same 2 and 2, just more air: wider gaps between signals and around the grid, so the card stands taller. */
  roomy?: boolean;
  conditionHeadline?: string;
  conditionBlurb?: string;
  nhdFindings?: NHDFindings;
  onTradeClick?: (trade: string) => void;
  repairPreviewActive?: boolean;
  onRepairPreviewToggle?: () => void;
}

const DISPLAY_LABELS: Record<string, string> = {
  HomeHealth: 'Home Health',
  RepairBurden: 'Repair Burden',
  LocationQuality: 'Location Quality',
  RiskExposure: 'Risk Exposure',
};

const URGENCY_COLOR: Record<string, string> = {
  Critical: '#DB5282', Urgent: '#FF9A4D', Moderate: '#FFF981',
  Low: '#5AE9FF', Monitor: '#9A938A',
};

const TRADE_ICON: Record<string, React.ReactNode> = {
  roofing: <Home size={14} />, roof: <Home size={14} />,
  electrical: <Zap size={14} />, electric: <Zap size={14} />,
  plumbing: <Droplets size={14} />,
  hvac: <Thermometer size={14} />, 'air conditioning': <Thermometer size={14} />,
  'air conditioning & heat': <Thermometer size={14} />, heating: <Thermometer size={14} />,
  structural: <Building2 size={14} />, structure: <Building2 size={14} />,
  foundation: <Layers size={14} />,
  pool: <Waves size={14} />, spa: <Waves size={14} />,
  pest: <Bug size={14} />,
  chimney: <Flame size={14} />, fireplace: <Flame size={14} />,
  garage: <Car size={14} />,
  landscaping: <Leaf size={14} />, exterior: <Leaf size={14} />,
  interior: <LayoutDashboard size={14} />,
};

const getTradeIcon = (trade: string) =>
  TRADE_ICON[trade.toLowerCase()] ?? <Home size={14} />;

const URGENCY_ORDER = ['Critical', 'Urgent', 'Moderate', 'Low', 'Monitor'];
const URGENCY_BAR: Record<string, string> = {
  Critical: '#DB5282', Urgent: '#FF9A4D', Moderate: '#FFF981',
  Low: '#5AE9FF', Monitor: '#9A938A',
};

const HH_HEADLINES: [number, string][] = [
  [90, 'Exceptionally well-maintained.'],
  [80, 'Strong bones, minor watch items.'],
  [70, 'Solid, some deferred maintenance.'],
  [60, 'Tired, not broken.'],
  [50, 'Showing its age.'],
  [0,  'Needs significant attention.'],
];
const RB_HEADLINES: [number, string][] = [
  [90, 'Minimal repair exposure.'],
  [80, 'Light maintenance horizon.'],
  [70, 'Manageable with planning.'],
  [60, 'Budget for near-term repairs.'],
  [50, 'Meaningful repair liability.'],
  [0,  'Heavy repair exposure.'],
];
const getHeadline = (score: number, tab: string, mode: string) => {
  if (mode === 'context') {
    const CTX_HEADLINES: Record<string, [number, string][]> = {
      LocationQuality: [
        [90, 'Prime location.'], [80, 'Strong location fundamentals.'],
        [70, 'Good area, some gaps.'], [60, 'Mixed location factors.'],
        [50, 'Below-average location.'], [0, 'Challenging location.'],
      ],
      RiskExposure: [
        [90, 'Minimal risk exposure.'], [80, 'Well-protected area.'],
        [70, 'Manageable risk profile.'], [60, 'Some risk factors present.'],
        [50, 'Elevated risk exposure.'], [0, 'High-risk area.'],
      ],
    };
    const table = CTX_HEADLINES[tab] ?? CTX_HEADLINES.LocationQuality;
    return (table.find(([min]) => score >= min) ?? table[table.length - 1])[1];
  }
  const table = tab === 'RepairBurden' ? RB_HEADLINES : HH_HEADLINES;
  return (table.find(([min]) => score >= min) ?? table[table.length - 1])[1];
};

const sentenceCaseHeadline = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  return trimmed
    .split(/(\s+)/)
    .map((part, index) => {
      if (/^\s+$/.test(part)) return part;
      const word = part.match(/[A-Za-z0-9&]+/)?.[0] ?? '';
      const isAcronym = word.length > 1 && word === word.toUpperCase() && /[A-Z]/.test(word);
      if (isAcronym) return part;
      const lowered = part.toLowerCase();
      return index === 0 ? lowered.charAt(0).toUpperCase() + lowered.slice(1) : lowered;
    })
    .join('');
};

// A signal column: one heading carries the meaning (its hairline in the column's colour, the label), then each signal
// as its icon, title and a two-line detail; no per-item markers
const SignalColumn = ({
  heading,
  color,
  items,
  roomy = false,
}: {
  heading: string;
  color: string;
  items: { label: string; detail?: string; icon: React.ReactNode }[];
  roomy?: boolean;
}) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: roomy ? 30 : 16, minWidth: 0 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: 'var(--tx-muted)' }}>
      <span style={{ width: 2, height: 12, borderRadius: 1, background: color }} />
      {heading}
    </div>
    {items.map((it, i) => (
      <div key={i} style={{ display: 'grid', gridTemplateColumns: '18px minmax(0, 1fr)', columnGap: 10, rowGap: 4 }}>
        <span style={{ display: 'flex', color: 'var(--tx-muted)', paddingTop: 1 }}>{it.icon}</span>
        {/* every item the same shape (a one-line title, a two-line detail) so switching tabs never changes the card's height */}
        <span
          title={it.label}
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--tx-text)',
            lineHeight: 1.35,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
          {it.label}
        </span>
        {it.detail && (
          <span
            style={{
              gridColumn: 2,
              fontSize: 12,
              fontWeight: 500,
              color: 'var(--tx-muted)',
              lineHeight: 1.5,
              minHeight: '3em',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}>
            {it.detail}
          </span>
        )}
      </div>
    ))}
  </div>
);

// The read's blurb, held to three lines; "More" opens the rest in place (the write-up itself should aim for two sentences)
const ReadBlurb = ({ text }: { text: string }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLParagraphElement>(null);
  const [clipped, setClipped] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (el && !open) setClipped(el.scrollHeight > el.clientHeight + 1);
  }, [text, open]);
  return (
    <div>
      <p
        ref={ref}
        style={{
          fontSize: 13,
          fontWeight: 500,
          color: 'var(--tx-muted)',
          lineHeight: 1.6,
          margin: 0,
          ...(open
            ? {}
            : { display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' as const, overflow: 'hidden' }),
        }}>
        {text}
      </p>
      {(clipped || open) && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          style={{
            marginTop: 4,
            padding: 0,
            border: 0,
            background: 'transparent',
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--tx-text)',
          }}>
          {open ? 'Less' : 'More'}
        </button>
      )}
    </div>
  );
};

export const SubscoreDetailCard = ({
  stories,
  isLoading = false,
  hasData = true,
  error = false,
  issueList = [],
  totalIssueCount,
  rawUrgencyCounts,
  mode = 'condition',
  roomy = false,
  conditionHeadline,
  conditionBlurb,
  nhdFindings: _nhdFindings,
  repairPreviewActive = false,
  onRepairPreviewToggle,
}: SubscoreDetailCardProps) => {
  const theme = useTheme();
  const _isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isMobile = window.innerWidth < 900;
  const { openAskHauserWithQuestion } = useDashboardLayout();
  const tabKeys = Object.keys(stories).filter((k) => !isHiddenSubscore(k));
  const [activeTab, setActiveTab] = useState<string>(tabKeys[0] ?? '');
  const [_accordion, _setAccordion] = useState<string | null>(null);
  const [selectedTrade, setSelectedTrade] = useState<string | null>(null);
  const [showNegotiationDialog, setShowNegotiationDialog] = useState(false);
  const [negotiationLeverage, setNegotiationLeverage] = useState({ min: 0, max: 0 });
  const [expandedDialogIdx, setExpandedDialogIdx] = useState<Set<number>>(new Set());
  const [_expanded, _setExpanded] = useState(false);
  const [pieOpen, setPieOpen] = useState(false);
  const [pieHoverIdx, setPieHoverIdx] = useState<number | null>(null);
  const [pieCardRect, setPieCardRect] = useState<DOMRect | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (tabKeys.length > 0 && !tabKeys.includes(activeTab)) {
      setActiveTab(tabKeys[0]);
    }
  }, [tabKeys, activeTab]);

  const toggleDialogRow = (idx: number) =>
    setExpandedDialogIdx((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });

  const toggleExposureBreakdown = () => {
    if (pieOpen) {
      setPieOpen(false);
      setPieHoverIdx(null);
      return;
    }

    if (cardRef.current) {
      const rect = cardRef.current.getBoundingClientRect();
      const contentTopOffset = 20 + 41 + 40;
      const horizontalInset = 20;
      const bottomInset = 20;
      setPieCardRect(new DOMRect(
        rect.left + horizontalInset,
        rect.top + contentTopOffset,
        Math.max(0, rect.width - horizontalInset * 2),
        Math.max(0, rect.height - contentTopOffset - bottomInset),
      ));
    }
    setPieOpen(true);
  };

  const tradeDialogIssues = selectedTrade
    ? [...issueList]
        .filter((i) => (i.trade ?? i.category) === selectedTrade)
        .sort((a, b) => {
          const uOrder = (u: string) =>
            u === 'Critical' ? 0 : u === 'Urgent' ? 1 : u === 'Moderate' ? 2
            : u === 'Low' ? 3 : 4;
          const uDiff = uOrder(a.urgency) - uOrder(b.urgency);
          return uDiff !== 0 ? uDiff : b.costMid - a.costMid;
        })
    : [];
  const dialogCostMin = tradeDialogIssues.reduce((s, i) => s + i.costMin, 0);
  const dialogCostMax = tradeDialogIssues.reduce((s, i) => s + i.costMax, 0);

  if (isLoading || (!hasData && !error)) {
    return (
      <div style={{ background: COLORS.card, borderRadius: 25, padding: 20, minHeight: 675 }}>
        {/* Pill tab selector skeleton */}
        <div
          style={{
            display: 'flex',
            background: COLORS.cardAlt,
            borderRadius: 15,
            padding: 3,
            marginBottom: 40,
          }}>
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              style={{
                flex: 1,
                padding: '0 12px',
                height: 35,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <ShimmerLine width="80%" height={14} />
            </div>
          ))}
        </div>
        {/* Large score shimmer */}
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, marginBottom: 8 }}>
          <ShimmerValue width={120} height={100} borderRadius={8} />
          <div style={{ paddingBottom: 16 }}>
            <span style={{ fontSize: 15, color: COLORS.textDim, opacity: 0.4 }}>/100</span>
          </div>
        </div>
        {/* Question shimmer */}
        <div style={{ textAlign: 'right', marginBottom: 16 }}>
          <ShimmerLine width="65%" height={13} style={{ marginLeft: 'auto' }} />
        </div>
        {/* Narrative box shimmer */}
        <div style={{ background: '#333333', borderRadius: 15, padding: 20, marginBottom: 20 }}>
          <ShimmerBlock
            lines={4}
            baseColor="rgba(255,255,255,0.06)"
            highlightColor="rgba(255,255,255,0.14)"
          />
        </div>
        {/* Driver section skeletons */}
        <div style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: 12 }}>
          <ShimmerLine width={160} height={13} style={{ marginBottom: 8 }} />
          <ShimmerLine width="90%" height={11} delay={0.1} />
          <ShimmerLine width="80%" height={11} delay={0.2} style={{ marginTop: 8 }} />
        </div>
        <div style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: 12, marginTop: 16 }}>
          <ShimmerLine width={160} height={13} style={{ marginBottom: 8 }} />
          <ShimmerLine width="85%" height={11} delay={0.1} />
          <ShimmerLine width="75%" height={11} delay={0.2} style={{ marginTop: 8 }} />
        </div>
      </div>
    );
  }

  if (!hasData) {
    return (
      <div
        style={{
          background: COLORS.card,
          borderRadius: 25,
          padding: 20,
          minHeight: 675,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <span style={{ color: COLORS.textDim, fontSize: 13 }}>
          {error ? 'Unable to load score details' : 'No score details available'}
        </span>
      </div>
    );
  }

  const ss = stories[activeTab];
  if (!ss) return null;

  // THE READ derived data
  const totalCount = issueList.length;
  const bigTicketCount = issueList.filter((i) => i.costMid > 3000).length;
  const critUrgCount = issueList.filter(
    (i) => i.urgency === 'Critical' || i.urgency === 'Urgent',
  ).length;
  const headline = getHeadline(ss.score, activeTab, mode);
  const readHeadline = sentenceCaseHeadline(
    (mode === 'condition' && activeTab === 'HomeHealth' && conditionHeadline)
      ? conditionHeadline
      : headline,
  );
  // 90-day repair exposure (RepairBurden tab only)
  const groupByTrade = (items: IssueItem[]) => {
    const map: Record<string, IssueItem[]> = {};
    for (const i of items) {
      const key = i.trade ?? i.category;
      if (!map[key]) map[key] = [];
      map[key].push(i);
    }
    return Object.entries(map)
      .map(([key, its]) => ({
        key,
        min: its.reduce((s, it) => s + it.costMin, 0),
        max: its.reduce((s, it) => s + it.costMax, 0),
        mid: its.reduce((s, it) => s + it.costMid, 0),
      }))
      .sort((a, b) => b.mid - a.mid);
  };
  const nowIssues = issueList.filter((i) => i.urgency === 'Critical');
  const soonIssues = issueList.filter((i) => i.urgency === 'Urgent');
  const nowGroups = groupByTrade(nowIssues);
  const soonGroups = groupByTrade(soonIssues);
  const nowMin = nowIssues.reduce((s, i) => s + i.costMin, 0);
  const nowMax = nowIssues.reduce((s, i) => s + i.costMax, 0);
  const soonMin = soonIssues.reduce((s, i) => s + i.costMin, 0);
  const soonMax = soonIssues.reduce((s, i) => s + i.costMax, 0);
  const totalRepairMin = nowMin + soonMin;
  const totalRepairMax = nowMax + soonMax;
  const fmtRange = (lo: number, hi: number) =>
    lo === 0 && hi === 0 ? 'N/A'
    : lo === hi ? `$${lo.toLocaleString()}`
    : `$${lo.toLocaleString()} – $${hi.toLocaleString()}`;

  const oneLiner = mode === 'context'
    ? (ss.strengths.length > 0 || ss.gaps.length > 0)
      ? `${ss.strengths.length} positive signal${ss.strengths.length !== 1 ? 's' : ''}, ` +
        `${ss.gaps.length} concern${ss.gaps.length !== 1 ? 's' : ''} flagged.`
      : ''
    : totalCount > 0
      ? (conditionBlurb ?? `Only ${critUrgCount} of ${totalCount} findings are Critical or Urgent — the score reflects accumulated wear, not active system failure.`)
      : '';
  const repairQuestions = [
    'What is the biggest condition risk in this home?',
    'Which of these repairs gives me the most negotiating leverage before closing?',
    'Are any of these items red flags that a lender or underwriter would flag?',
    'Could any of these items affect my homeowner\'s insurance eligibility or premium?',
  ];


  return (
    <div
      ref={cardRef}
      data-testid="subscore-detail-card"
      style={{
        background: COLORS.card,
        backgroundImage: 'var(--tx-surface-light)',
        boxShadow: 'var(--tx-shadow)',
        borderRadius: '24px',
        padding: 20,
        overflow: 'hidden',
        position: 'relative',
      }}>
      {/* the view switch: the Tradehaus segment (a tonal track, the current view a raised thumb), compact and left */}
      <div
        role="tablist"
        style={{
          display: 'inline-flex',
          gap: 2,
          background: 'var(--tx-fill)',
          borderRadius: '12px',
          padding: 3,
          marginBottom: 24,
        }}>
        {tabKeys.map((tabKey) => {
          const isActive = activeTab === tabKey;
          return (
            <button
              key={tabKey}
              data-testid={`subscore-tab-${tabKey.toLowerCase()}`}
              onClick={() => {
                setActiveTab(tabKey);
                _setAccordion(null);
                _setExpanded(false);
                setPieOpen(false);
                setPieHoverIdx(null);
              }}
              role="tab"
              aria-selected={isActive}
              style={{
                padding: '0 14px',
                height: 32,
                borderRadius: '9px',
                border: 'none',
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: 13,
                // one weight for every tab (the ds Segmented): selection shows in ink and the raised tile, so tabs never resize
                fontWeight: 600,
                color: isActive ? 'var(--tx-text)' : 'var(--tx-muted)',
                background: isActive ? 'var(--tx-sel-bg)' : 'transparent',
                boxShadow: isActive ? 'var(--tx-sel-shadow)' : 'none',
                transition: 'all 0.15s',
              }}>
              {(DISPLAY_LABELS[tabKey] || tabKey).replace(/ (\w)/g, (m) => m.toLowerCase())}
            </button>
          );
        })}
      </div>

      {/* THE READ — condition mode only; context mode uses standalone CTA below. A two-column story: the words on the
          left (eyebrow, headline, the blurb right under it), the findings mix beside them as their evidence */}
      {ss.score > 0 && activeTab !== 'RepairBurden' && mode === 'condition' && (() => {
        const getUrgencyCnt = (u: string) =>
          rawUrgencyCounts ? (rawUrgencyCounts[u] ?? 0) : issueList.filter((i) => i.urgency === u).length;
        const mixTotal = URGENCY_ORDER.reduce((sum, u) => sum + getUrgencyCnt(u), 0) || 1;
        return (
          <div
            style={{
              display: 'grid',
              // phone: the story over its evidence, each at full width
              gridTemplateColumns: totalCount > 0 && !isMobile ? 'minmax(0, 1fr) 200px' : '1fr',
              gap: isMobile ? 24 : 32,
              marginBottom: 8,
            }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-muted)', marginBottom: 10 }}>The read</div>
              <div
                style={{
                  fontSize: isMobile ? 20 : 22,
                  fontWeight: 300,
                  letterSpacing: '-0.02em',
                  lineHeight: 1.3,
                  color: 'var(--tx-text)',
                  marginBottom: 12,
                  textWrap: 'balance',
                }}>
                {readHeadline}
              </div>
              {oneLiner ? <ReadBlurb text={oneLiner} /> : null}
            </div>
            {totalCount > 0 && (
              // stretches to the story's height; its five rows spread across it so both columns start and end together
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-muted)' }}>Findings</span>
                  <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-faint)' }}>
                    {totalIssueCount ?? totalCount} · {bigTicketCount} big-ticket
                  </span>
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 9 }}>
                  {URGENCY_ORDER.map((u) => {
                    const cnt = getUrgencyCnt(u);
                    return (
                      <div key={u} style={{ display: 'grid', gridTemplateColumns: '1fr 56px 24px', alignItems: 'center', gap: 10 }}>
                        <LegendItem color={URGENCY_BAR[u]} label={u} />
                        <span style={{ height: 2, borderRadius: 1, background: 'var(--tx-fill-2)', position: 'relative' }}>
                          <span
                            style={{
                              position: 'absolute',
                              inset: '0 auto 0 0',
                              width: `${(cnt / mixTotal) * 100}%`,
                              borderRadius: 1,
                              background: URGENCY_BAR[u],
                            }}
                          />
                        </span>
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: cnt ? 'var(--tx-text)' : 'var(--tx-faint)',
                            textAlign: 'right',
                            fontVariantNumeric: 'tabular-nums',
                          }}>
                          {cnt}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {mode === 'condition' && activeTab !== 'RepairBurden' && (
        <>
          {/* Ask Hauser: an eyebrow with the small brand mark, then the questions as a quiet hairline list (Tradehaus
              rows: 13/500 in ink, a chevron, hover by fill), not a stack of filled bars */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            marginTop: 24, marginBottom: 4, paddingTop: 20, borderTop: '1px solid var(--tx-line)',
            color: 'var(--tx-muted)', fontSize: 12, fontWeight: 600,
          }}>
            <svg width="16" height="16" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M50 100C77.6142 100 100 77.6142 100 50C100 22.3858 77.6142 0 50 0C22.3858 0 0 22.3858 0 50C0 77.6142 22.3858 100 50 100Z" fill="#C0FF02"/>
              {/* eslint-disable-next-line max-len */}
              <path d="M56.2133 22.2267H43.7733L31.4266 77.7867H68.56L56.2133 22.2267ZM44.2266 67.7067L49.7066 40.3067L49.96 38.32L50.2133 40.3067L55.76 67.7067H44.24H44.2266Z" fill="#262626"/>
            </svg>
            <span>Ask Hauser</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {repairQuestions.map((q, i) => (
              <button
                key={i}
                type="button"
                onClick={() => openAskHauserWithQuestion(q)}
                className="hx-ask-row"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                  padding: '6px 0', margin: 0,
                  border: 0, borderTop: 'none', borderRadius: 0,
                  background: 'transparent', cursor: 'pointer', textAlign: 'left',
                  fontFamily: 'inherit', fontSize: 12, fontWeight: 500, lineHeight: 1.4,
                }}>
                <span style={{ flex: 1 }}>{q}</span>
                <ChevronDown size={12} style={{ transform: 'rotate(-90deg)', color: 'var(--tx-faint)', flexShrink: 0 }} />
              </button>
            ))}
          </div>
          {/* rows stay on the card's edge; hover brightens the question and its chevron rather than adding a fill */}
          <style>
            {'.hx-ask-row { color: var(--tx-muted); transition: color 0.12s ease; } .hx-ask-row:hover { color: var(--tx-text) !important; } ' +
              '.hx-ask-row > svg { transition: color 0.12s ease, transform 0.12s ease; } ' +
              '.hx-ask-row:hover > svg { color: var(--tx-text) !important; transform: rotate(-90deg) translateY(2px) !important; }'}
          </style>
          <button
            type="button"
            onClick={() => {
              openAskHauserWithQuestion(
                `Give me a structured property summary using exactly these four plain-text sections. ` +
                `No markdown, no bold, no bullet points — just the labels and prose:\n\n` +
                `THE OVERVIEW\n[What the condition and location scores say together about this home.]\n\n` +
                `BIGGEST RISK\n[The single finding or pattern a buyer cannot ignore.]\n\n` +
                `REPAIR LEVERAGE\n[What's negotiable, what isn't, and the rough cost exposure.]\n\n` +
                `THE VERDICT\n[Direct framing: buy, walk, or negotiate — and why.]\n\n` +
                `Each section 2–3 sentences. Be direct, no filler.`,
                'Get the full property report',
              );
            }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, alignSelf: 'flex-start',
              marginTop: 14, padding: 0, border: 0, background: 'transparent', cursor: 'pointer',
              fontFamily: 'inherit', fontSize: 12, fontWeight: 600, color: 'var(--tx-muted)',
            }}>
            Full property report
            <ChevronDown size={11} style={{ transform: 'rotate(-90deg)' }} />
          </button>
        </>
      )}


      {/* RepairBurden: exposure header */}
      {mode === 'condition' && activeTab === 'RepairBurden' && (() => {
        const allBuckets = [
          { id: 'now', label: 'NOW', sub: '< 30 days', color: '#DB5282',
            issues: nowIssues, groups: nowGroups, min: nowMin, max: nowMax },
          { id: 'soon', label: 'SOON', sub: '1–3 mo', color: '#FF9A4D',
            issues: soonIssues, groups: soonGroups, min: soonMin, max: soonMax },
        ];
        const totalMid = allBuckets.reduce((s, b) => s + (b.min + b.max) / 2, 0) || 1;
        const hasExposure = totalRepairMin > 0 || totalRepairMax > 0;

        return (
          <>
            <div style={{ position: 'relative' }}>
            {/* 90-Day Exposure card */}
            <div style={{ marginBottom: 16 }}>
            <div>
              {/* Header row */}
              <div style={{ display: 'flex', justifyContent: 'space-between',
                alignItems: 'flex-start', marginBottom: 16 }}>
                {/* Title + pie button */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-muted)' }}>90-day exposure</span>
                  {hasExposure && (
                    <button
                      onClick={toggleExposureBreakdown}
                      title="View breakdown by trade"
                      style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: 22, height: 22, borderRadius: '50%',
                        background: 'transparent', border: 'none',
                        color: 'var(--tx-muted)', cursor: 'pointer', padding: 0,
                        fontFamily: "'Manrope',sans-serif",
                      }}
                    >
                      <PieChartIcon size={14} strokeWidth={1.6} />
                    </button>
                  )}
                </div>
                {hasExposure && (
                  <div style={{ textAlign: 'right' }}>
                    <div
                      style={{
                        fontFamily: VALUE_FONT_SMALL,
                        fontSize: 20,
                        fontWeight: 200,
                        letterSpacing: '-0.04em',
                        color: 'var(--tx-text)',
                        lineHeight: 1,
                      }}>
                      {fmtRange(totalRepairMin, totalRepairMax)}
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-muted)', marginTop: 6 }}>
                      Combined estimate
                    </div>
                  </div>
                )}
              </div>
              {/* Progress bar — segmented with gap + glow */}
              <div style={{ display: 'flex', gap: 2, height: 4, borderRadius: 2,
                overflow: 'hidden', marginBottom: 16 }}>
                {allBuckets.map((b) => {
                  const mid = (b.min + b.max) / 2;
                  const pct = mid / totalMid * 100;
                  return pct > 0 ? (
                    <div key={b.id} style={{
                      width: `${pct}%`, background: b.color,
                    }} />
                  ) : null;
                })}
                {!hasExposure && (
                  <div style={{ width: '100%', background: 'var(--tx-fill-2)' }} />
                )}
              </div>
              {/* Bucket stats */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
                {allBuckets.map((b) => (
                  <div key={b.id}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                      <span style={{ width: 2, height: 12, borderRadius: 1, background: b.color, flexShrink: 0 }} />
                      <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-muted)' }}>
                        {b.label.charAt(0) + b.label.slice(1).toLowerCase()}
                        <span style={{ color: 'var(--tx-faint)' }}> · {b.sub}</span>
                      </span>
                    </div>
                    <div style={{ fontFamily: VALUE_FONT_SMALL, fontSize: 17, fontWeight: 300,
                      letterSpacing: '-0.03em', color: 'var(--tx-text)', marginBottom: 4 }}>
                      {b.issues.length > 0 ? fmtRange(b.min, b.max) : '—'}
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-muted)' }}>
                      {b.issues.length > 0
                        ? `${b.issues.length} item${b.issues.length !== 1 ? 's' : ''}`
                        : 'none flagged'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            </div>

            </div>

            {/* Card takeover portal — mirrors RFQ Builder pattern */}
            {pieOpen && pieCardRect && (() => {
              const NEUTRAL_POOL = ['#BBB7AF','#8B8680','#D6D2C9','#9C8E7A','#A89E92','#5A5852','#E8E3D8'];
              const pool = issueList.filter((i) => i.urgency === 'Critical' || i.urgency === 'Urgent');
              const buckets = new Map<string, { name: string; count: number; value: number }>();
              for (const it of pool) {
                const k = it.trade ?? it.category;
                const label = k.replace(/_/g, ' ');
                const prev = buckets.get(k) ?? { name: label, count: 0, value: 0 };
                prev.count += 1;
                prev.value += it.costMid;
                buckets.set(k, prev);
              }
              const sorted = Array.from(buckets.values()).sort((a, b) => b.value - a.value);
              const segments: { name: string; count: number; value: number; color: string }[] = [];
              for (let i = 0; i < sorted.length; i += 1) {
                const prev = segments[i - 1]?.color ?? null;
                const wrap = i === sorted.length - 1 ? (segments[0]?.color ?? null) : null;
                let color = NEUTRAL_POOL[i % NEUTRAL_POOL.length];
                for (let off = 0; off < NEUTRAL_POOL.length; off += 1) {
                  const c = NEUTRAL_POOL[(i + off) % NEUTRAL_POOL.length];
                  if (c !== prev && c !== wrap) { color = c; break; }
                }
                segments.push({ ...sorted[i], color });
              }
              const grandTotal = segments.reduce((s, x) => s + x.value, 0) || 1;
              const fmtK = (n: number) =>
                n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M`
                : n >= 1000 ? `$${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}K`
                : `$${Math.round(n).toLocaleString()}`;
              if (!segments.length) return null;

              const padX = 32;
              const reservedV = 32;
              const cardW = pieCardRect.width - padX;
              const cardH = pieCardRect.height - reservedV;
              const fitSize = Math.max(160, Math.min(cardW, cardH));
              const size = Math.min(fitSize, 420);
              const outer = Math.floor(size / 2);
              const inner = Math.floor(outer * 0.65);

              return createPortal(
                <>
                  <style>{`
                    .repair-burden-donut-overlay .recharts-wrapper,
                    .repair-burden-donut-overlay .recharts-wrapper *,
                    .repair-burden-donut-overlay svg,
                    .repair-burden-donut-overlay path {
                      outline: none !important;
                      -webkit-tap-highlight-color: transparent;
                    }
                  `}</style>
                  {/* Frosted backdrop */}
                  <div
                    onClick={() => { setPieOpen(false); setPieHoverIdx(null); }}
                    style={{
                      position: 'fixed',
                      top: pieCardRect.top, left: pieCardRect.left,
                      width: pieCardRect.width, height: pieCardRect.height,
                      borderRadius: '12px',
                      background: 'var(--tx-glass-panel)',
                      backgroundImage: 'var(--tx-surface-light)',
                      boxShadow: 'var(--tx-shadow)',
                      backdropFilter: 'blur(8px) saturate(140%)',
                      WebkitBackdropFilter: 'blur(8px) saturate(140%)',
                      zIndex: 1400,
                    }}
                  />
                  {/* Content */}
                  <div
                    className="repair-burden-donut-overlay"
                    style={{
                      position: 'fixed',
                      top: pieCardRect.top, left: pieCardRect.left,
                      width: pieCardRect.width, height: pieCardRect.height,
                      borderRadius: 10,
                      zIndex: 1401,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <CloseButton
                      size="sm"
                      onClose={() => { setPieOpen(false); setPieHoverIdx(null); }}
                      label="Close"
                      sx={{ position: 'absolute', top: 8, right: 8 }}
                    />

                    <div style={{
                      position: 'relative',
                      width: size, height: size, flexShrink: 0,
                    }}>
                      {/* Blur ring masked to donut shape */}
                      <div
                        aria-hidden
                        style={{
                          position: 'absolute', inset: 0, pointerEvents: 'none',
                          backdropFilter: 'blur(12px) saturate(150%)',
                          WebkitBackdropFilter: 'blur(12px) saturate(150%)',
                          WebkitMaskImage: `radial-gradient(circle at center, transparent ${inner - 1}px, black ${
                            inner + 1}px, black ${outer - 1}px, transparent ${outer + 1}px)`,
                          maskImage: `radial-gradient(circle at center, transparent ${inner - 1}px, black ${
                            inner + 1}px, black ${outer - 1}px, transparent ${outer + 1}px)`,
                        }}
                      />
                      {/* Static center text */}
                      <div style={{
                        position: 'absolute', inset: 0,
                        display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center',
                        textAlign: 'center', pointerEvents: 'none',
                        fontFamily: "'Manrope', sans-serif",
                      }}>
                        <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.18em',
color: 'var(--tx-muted)', lineHeight: 1.3 }}>
                          90-Day Exposure
                        </div>
                        <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.18em',
color: 'var(--tx-muted)', marginTop: 1 }}>
                          by Trade
                        </div>
                        <div style={{ fontSize: 28, fontWeight: 600, color: COLORS.text,
                          letterSpacing: '-0.02em', marginTop: 10, lineHeight: 1 }}>
                          {fmtK(grandTotal)}
                        </div>
                      </div>
                      {/* Chart */}
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={segments}
                            cx="50%" cy="50%"
                            innerRadius={inner} outerRadius={outer}
                            paddingAngle={4}
                            cornerRadius={2}
                            dataKey="value"
                            onMouseEnter={(_, idx) => setPieHoverIdx(idx)}
                            onMouseLeave={() => setPieHoverIdx(null)}
                          >
                            {segments.map((s, i) => (
                              <Cell
                                key={s.name}
                                fill={pieHoverIdx === i ? '#C0FF02' : s.color}
                                stroke="none"
                                style={{ transition: 'fill 0.15s' }}
                              />
                            ))}
                          </Pie>
                          <ReTooltip
                            cursor={{ fill: 'transparent' }}
                            isAnimationActive={false}
                            content={({ active, payload }) => {
                              if (!active || !payload?.length) return null;
                              const d = payload[0].payload as typeof segments[0];
                              const pct = (d.value / grandTotal) * 100;
                              return (
                                <div style={{
                                  background: 'var(--tx-glass-panel)',
                                  backgroundImage: 'var(--tx-surface-light)',
                                  boxShadow: 'var(--tx-shadow)',
                                  backdropFilter: 'blur(10px)',
                                  WebkitBackdropFilter: 'blur(10px)',
                                  borderRadius: 8, padding: '10px 14px',
                                  color: 'var(--tx-text)', fontFamily: "'Manrope',sans-serif",
                                  whiteSpace: 'nowrap', pointerEvents: 'none',
                                  display: 'flex', alignItems: 'center', gap: 14,
                                }}>
                                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <div style={{ fontSize: 11, fontWeight: 600,
                                      letterSpacing: 0.3,
                                      color: 'var(--tx-text)', marginBottom: 3 }}>
                                      {d.name}
                                    </div>
                                    <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-muted)' }}>
                                      {d.count} item{d.count === 1 ? '' : 's'} ·{' '}
                                      <span style={{ color: 'var(--tx-text)' }}>{pct.toFixed(pct < 10 ? 1 : 0)}%</span>
                                    </div>
                                  </div>
                                  <div style={{ width: 1, alignSelf: 'stretch',
                                    background: 'var(--tx-fill-3)' }} />
                                  <div style={{ fontSize: 28, fontWeight: 600,
                                    lineHeight: 1, letterSpacing: '-0.02em' }}>
                                    {fmtK(d.value)}
                                  </div>
                                </div>
                              );
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </>,
                document.body,
              );
            })()}
          </>
        );
      })()}

      {/* Context mode: LocationQuality — compact signal chips */}
      {mode === 'context' && activeTab !== 'RiskExposure' && (() => {
        const getLocationIcon = (label: string): React.ReactNode => {
          const t = label.toLowerCase();
          if (t.includes('school') || t.includes('childcare') || t.includes('education')) return <GraduationCap size={15} />;
          if (t.includes('fire')) return <Flame size={15} />;
          if (t.includes('police') || t.includes('law enforcement') || t.includes('crime') || t.includes('safety')) return <Shield size={15} />;
          if (t.includes('transit') || t.includes('bus') || t.includes('train')) return <Bus size={15} />;
          if (t.includes('walk')) return <Footprints size={15} />;
          if (t.includes('park') || t.includes('green space') || t.includes('trail')) return <Trees size={15} />;
          if (t.includes('grocery') || t.includes('shopping')) return <ShoppingCart size={15} />;
          if (t.includes('restaurant') || t.includes('dining') || t.includes('food')) return <Utensils size={15} />;
          if (t.includes('noise') || t.includes('airport')) return <Volume2 size={15} />;
          if (t.includes('hospital') || t.includes('medical') || t.includes('health')) return <Stethoscope size={15} />;
          if (t.includes('landmark') || t.includes('amenit')) return <Landmark size={15} />;
          return <MapPin size={15} />;
        };

        const allSignals = [
          ...ss.strengths.slice(0, 2).map((s) => ({ text: s, dir: 'up' as const })),
          ...ss.gaps.slice(0, 2).map((s) => ({ text: s, dir: 'down' as const })),
        ];
        if (!allSignals.length) return null;
        return (
          // what works for the location and what works against it, side by side
          (() => {
            const split = (sig: { text: string }) => {
              const d = sig.text.indexOf(' — ');
              const label = d !== -1 ? sig.text.slice(0, d) : sig.text;
              return { label, detail: d !== -1 ? sig.text.slice(d + 3) : undefined, icon: getLocationIcon(label) };
            };
            const ups = allSignals.filter((x) => x.dir === 'up').map(split);
            const downs = allSignals.filter((x) => x.dir === 'down').map(split);
            return (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                  columnGap: roomy ? 40 : 32,
                  rowGap: 28,
                  padding: roomy ? '8px 0 12px' : undefined,
                }}>
                {ups.length > 0 && <SignalColumn heading="Working for it" color={COLORS.positive} items={ups} roomy={roomy} />}
                {downs.length > 0 && (
                  <SignalColumn heading="Working against it" color={COLORS.critical} items={downs} roomy={roomy} />
                )}
              </div>
            );
          })()
        );
      })()}

      {/* Context mode: RiskExposure — NHD-sourced risk tiles (falls back to hauser-eq signals) */}
      {mode === 'context' && activeTab === 'RiskExposure' && (() => {
        const getNHDIcon = (label: string): React.ReactNode => {
          const t = label.toLowerCase();
          if (t.includes('flood') || t.includes('fema')) return <Droplets size={15} />;
          if (t.includes('fire') || t.includes('fhsz') || t.includes('wildfire')) return <Flame size={15} />;
          if (t.includes('seismic') || t.includes('earthquake') || t.includes('fault')) return <Activity size={15} />;
          if (t.includes('radon') || t.includes('air') || t.includes('contaminat') || t.includes('methane')) return <Wind size={15} />;
          if (t.includes('landslide') || t.includes('erosion') || t.includes('soil')) return <Mountain size={15} />;
          if (t.includes('crime') || t.includes('safety') || t.includes('burglary') || t.includes('theft') || t.includes('violent')) return <Shield size={15} />;
          if (t.includes('dam') || t.includes('inundation')) return <Droplets size={15} />;
          if (t.includes('wind') || t.includes('storm')) return <Zap size={15} />;
          if (t.includes('airport') || t.includes('noise')) return <Car size={15} />;
          if (t.includes('tax') || t.includes('mello') || t.includes('assessment')) return <Building2 size={15} />;
          return <Layers size={15} />;
        };

        // Critical NHD findings (inZone=true only) take up to 3 spots.
        // Remaining slots filled by hauser-eq risk signals. Total cap: 6.
        const CRITICAL_NHD_KEYWORDS = ['fire', 'flood', 'fema', 'seismic', 'earthquake', 'fault',
          'radon', 'contaminat', 'landslide', 'tsunami', 'methane', 'hazardous liquid', 'wildfire'];
        const isCriticalNHD = (label: string) => {
          const t = label.toLowerCase();
          return CRITICAL_NHD_KEYWORDS.some((kw) => t.includes(kw));
        };

        type RiskLevel = 'high' | 'mid' | 'low';
        type RiskTile = { label: string; detail?: string; icon: React.ReactNode; risk: RiskLevel };
        const RISK_CHIP: Record<RiskLevel, { label: string; color: string }> = {
          high: { label: 'High risk', color: '#DB5282' },
          mid: { label: 'Mid risk', color: '#FF9A4D' },
          low: { label: 'Low risk', color: '#5AE9FF' },
        };

        const NHD_FALLBACK: [string, string][] = [
          ['gas and hazardous liquid', 'Pipeline in the area — review disclosure for proximity and safety details.'],
          ['pipeline', 'Pipeline in the area — review disclosure for proximity and safety details.'],
          ['methane', 'Property is in a methane gas area — ventilation and construction standards apply.'],
          ['megan', 'Statutory notification zone — registered offenders may be present nearby.'],
          ['right to farm', 'Agricultural operations within 1 mile may produce noise, odor, or dust.'],
          ['commercial/industrial', 'Commercial or industrial land use within 1 mile may affect air quality and noise.'],
          ['military ordnance', 'Former military site within 1 mile — unexploded ordnance risk disclosure required.'],
          ['mining', 'Active or former mining operations within 1 mile.'],
          ['sustainable groundwater', 'Property falls under the Sustainable Groundwater Management Act area.'],
          ['airport', 'Within an airport influence area — potential noise and flight path exposure.'],
          ['tsunami', 'Tsunami inundation hazard area — review local evacuation routes.'],
          ['critical habitat', 'In or near a protected critical habitat area — may affect landscaping or development.'],
          ['radon', 'Elevated radon potential — EPA recommends testing all homes before purchase.'],
          ['flood', 'FEMA-designated flood area — flood insurance may be required by lender.'],
        ];
        const getNHDFallbackDetail = (label: string): string | undefined => {
          const t = label.toLowerCase();
          return NHD_FALLBACK.find(([kw]) => t.includes(kw))?.[1];
        };

        const tiles: RiskTile[] = [];

        if (_nhdFindings) {
          const allNHD = [
            ...Object.values(_nhdFindings.influencingSubfactors),
            ..._nhdFindings.disclosures,
          ];
          const critical = allNHD
            .filter((f) => f.inZone && isCriticalNHD(f.label))
            .slice(0, 3);
          for (const f of critical) {
            const rawDetail = [f.zone, f.detail].filter(Boolean).join(' — ');
            const detail = rawDetail || getNHDFallbackDetail(f.label);
            tiles.push({ label: f.label, detail, icon: getNHDIcon(f.label), risk: 'high' });
          }
        }

        const hqGaps = ss.gaps.slice(0, 2).map((s) => ({ text: s, risk: 'mid' as RiskLevel }));
        const hqStrengths = ss.strengths.slice(0, 2).map((s) => ({ text: s, risk: 'low' as RiskLevel }));
        for (const sig of [...hqGaps, ...hqStrengths]) {
          if (tiles.length >= 4) break;
          const dashIdx = sig.text.indexOf(' — ');
          const label = dashIdx !== -1 ? sig.text.slice(0, dashIdx) : sig.text;
          const detail = dashIdx !== -1 ? sig.text.slice(dashIdx + 3) : undefined;
          tiles.push({ label, detail, icon: getNHDIcon(label), risk: sig.risk });
        }

        if (!tiles.length) return null;
        return (
          // risks grouped by level, each group headed once by its colour and name
          // the same grid as Location quality's, 27" spacing included, so switching tabs doesn't move a thing
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              columnGap: roomy ? 40 : 32,
              rowGap: 28,
              padding: roomy ? '8px 0 12px' : undefined,
            }}>
            {(['high', 'mid', 'low'] as RiskLevel[])
              .map((lvl) => ({ lvl, items: tiles.filter((t) => t.risk === lvl) }))
              .filter((g) => g.items.length > 0)
              .map((g) => (
                <SignalColumn
                  key={g.lvl}
                  heading={RISK_CHIP[g.lvl].label}
                  color={RISK_CHIP[g.lvl].color}
                  items={g.items}
                  roomy={roomy}
                />
              ))}
          </div>
        );
      })()}

      {/* Context mode: standalone Ask Hauser CTA (replaces THE READ card) */}
      {mode === 'context' && ss.score > 0 && (
        <>
        <div
          onClick={() => {
            const isRisk = activeTab === 'RiskExposure';
            openAskHauserWithQuestion(
              isRisk
                ? `Give me a structured risk analysis for this property using exactly these four plain-text sections. ` +
                  `No markdown, no bold, no bullet points — just the labels and prose:\n\n` +
                  `FLOOD & WATER\n[Flood zone designation, FEMA status, and any water/drainage risks on or near the property.]\n\n` +
                  `FIRE & SEISMIC\n[Wildfire hazard zone, fire history, fault proximity, and seismic shaking potential.]\n\n` +
                  `ENVIRONMENTAL\n[Radon, air quality, soil contamination, or other environmental hazard disclosures.]\n\n` +
                  `THE VERDICT\n[Is risk exposure a dealbreaker, a pricing factor, or manageable — and what should the buyer do about it.]\n\n` +
                  `Each section 2–3 sentences. Be direct, no filler.`
                : `Give me a structured location and context report using exactly these three plain-text sections. ` +
                  `No markdown, no bold, no bullet points — just the labels and prose:\n\n` +
                  `THE LOCATION\n[What the location quality score says about walkability, schools, transit, amenities, and neighborhood character.]\n\n` +
                  `RISK EXPOSURE\n[The most important environmental, safety, or natural hazard risks for this property and area.]\n\n` +
                  `THE VERDICT\n[Is location a reason to buy, a concern to price in, or a neutral factor — and why.]\n\n` +
                  `Each section 2–3 sentences. Be direct, no filler.`,
              isRisk ? 'Get the full risk analysis' : 'Get the full location report',
            );
          }}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            marginTop: 24,
            cursor: 'pointer',
            fontSize: 12, fontWeight: 600, color: 'var(--tx-muted)',
          }}>
          {activeTab === 'RiskExposure' ? 'Full risk analysis' : 'Full location report'}
          <ChevronDown size={11} style={{ transform: 'rotate(-90deg)' }} />
        </div>
        </>
      )}

      {/* Condition mode: Top Concerns snapshot (RepairBurden) */}
      {mode === 'condition' && activeTab === 'RepairBurden' && (() => {
        const { topGroups, leverageMin, leverageMax } = computeTopConcernGroups(issueList);

        if (topGroups.length === 0) return null;

        return (
          <div style={{ paddingTop: 12 }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
              marginBottom: 10,
            }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-muted)' }}>Top concerns</div>
              {onRepairPreviewToggle && (
                <Tip
                  text="Shows the score impact if these Top Concerns are fixed."
                  placement="top"
                  fixedWidth={260}
                  tooltipContainerRef={cardRef}
                  style={{ borderBottom: 'none', cursor: 'pointer' }}>
                  <label
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      color: 'var(--tx-muted)',
                      fontSize: 12,
                      fontWeight: 500,
                      fontFamily: "'Manrope', sans-serif",
                      cursor: 'pointer',
                      letterSpacing: 0,
                      whiteSpace: 'nowrap',
                    }}>
                    <span>Simulate repairs</span>
                    <Switch
                      size="small"
                      checked={repairPreviewActive}
                      onClick={(event) => event.stopPropagation()}
                      onChange={onRepairPreviewToggle}
                      inputProps={{ 'aria-label': 'Simulate repairs' }}
                    />
                  </label>
                </Tip>
              )}
            </div>
            {topGroups.map((g, i) => {
              const hasCost = g.totalMin > 0 || g.totalMax > 0;
              return (
                <div
                  key={g.key}
                  onClick={() => { setSelectedTrade(g.key); setExpandedDialogIdx(new Set()); }}
                  style={{ padding: '10px 0',
                    borderTop: i > 0 ? '1px solid var(--tx-line)' : 'none',
                    display: 'flex', alignItems: 'center', gap: 10,
                    cursor: 'pointer' }}>
                  {/* the trade's hairline icon on its own, no tile behind it */}
                  <span style={{ width: 18, flexShrink: 0, display: 'flex', justifyContent: 'flex-start', color: 'var(--tx-muted)' }}>
                    {getTradeIcon(g.key)}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx-text)', flex: 1 }}>
                    {g.key}
                  </span>
                  {hasCost && (
                    <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--tx-muted)', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                      {fmtRange(g.totalMin, g.totalMax)}
                    </span>
                  )}
                </div>
              );
            })}
            {(leverageMin > 0 || leverageMax > 0) && (
              <div style={{
                marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--tx-line)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
              }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-muted)' }}>Negotiation leverage</span>
                  <button
                    onClick={() => {
                      setNegotiationLeverage({ min: leverageMin, max: leverageMax });
                      setShowNegotiationDialog(true);
                    }}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4, alignSelf: 'flex-start',
                      background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
                      fontFamily: 'inherit', fontSize: 12, fontWeight: 600, color: 'var(--tx-text)',
                    }}>
                    Open negotiation tool
                    <ArrowRight size={12} />
                  </button>
                </div>
                <span style={{ fontFamily: VALUE_FONT_SMALL, fontSize: 20, fontWeight: 200,
                  letterSpacing: '-0.04em', color: 'var(--tx-text)', flexShrink: 0, lineHeight: 1 }}>
                  {fmtRange(leverageMin, leverageMax)}
                </span>
              </div>
            )}
          </div>
        );
      })()}

      {/* Trade detail dialog */}
      <Dialog
        open={!!selectedTrade}
        onClose={() => setSelectedTrade(null)}
        width={576}
        containerProps={{ p: '0px', gap: 0 }}
        header={
          <div style={{ padding: '20px 48px 14px 20px',
            borderBottom: `1px solid ${COLORS.border}`,
            display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{
              width: 32, height: 32, borderRadius: 10, flexShrink: 0,
              background: 'var(--tx-fill)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: COLORS.text,
            }}>
              {selectedTrade ? getTradeIcon(selectedTrade) : null}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: COLORS.text }}>
                {selectedTrade}
              </div>
              <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 1 }}>
                {tradeDialogIssues.length} finding{tradeDialogIssues.length !== 1 ? 's' : ''}
                {(dialogCostMin > 0 || dialogCostMax > 0)
                  ? ` · ${fmtRange(dialogCostMin, dialogCostMax)}` : ''}
              </div>
            </div>
          </div>
        }>
        {/* Issue list */}
        <ScrollHintList style={{ maxHeight: 504, overflowY: 'auto', display: 'flex',
          flexDirection: 'column', gap: 6, padding: '10px 20px 20px' }}>
          {tradeDialogIssues.map((issue, i) => {
            const col = URGENCY_COLOR[issue.urgency] ?? COLORS.textDim;
            const isExp = expandedDialogIdx.has(i);
            const hasCost = issue.costMin > 0 || issue.costMax > 0;
            const hasDesc = !!issue.description?.trim();
            return (
              <div key={i} style={{ marginBottom: i < tradeDialogIssues.length - 1 ? 5 : 0 }}>
                <div
                  onClick={() => toggleDialogRow(i)}
                  onMouseEnter={(e) => {
                    if (!isExp) e.currentTarget.style.background = '#333';
                  }}
                  onMouseLeave={(e) => {
                    if (!isExp) e.currentTarget.style.background = '#2a2a2a';
                  }}
                  style={{
                    display: 'flex', alignItems: 'center',
                    padding: '10px 14px', gap: 10,
                    background: isExp ? 'var(--tx-fill)' : 'var(--tx-glass-panel)',
                    borderRadius: isExp && hasDesc ? '10px 10px 0 0' : 10,
                    cursor: 'pointer', transition: 'background 0.15s',
                  }}>
                  <span style={{ width: 3, height: 24, borderRadius: 2,
                    background: col, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.text }}>
                      {issue.title}
                    </div>
                    <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 2 }}>
                      {(issue.trade ?? issue.category)?.replace(/_/g, ' ')}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column',
                    alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.text,
                      fontFamily: "'Manrope', sans-serif" }}>
                      {hasCost ? fmtRange(issue.costMin, issue.costMax) : '—'}
                    </span>
                    <span style={{
                      color: COLORS.textDim, display: 'inline-flex', alignItems: 'center',
                      transform: isExp ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s',
                    }}>
                      <ChevronDown size={14} />
                    </span>
                  </div>
                </div>
                {isExp && hasDesc && (
                  <div style={{
                    background: 'var(--tx-glass-panel)', padding: '12px 14px 14px 26px',
                    borderRadius: '0 0 10px 10px',
                    fontSize: 12, color: COLORS.text, lineHeight: 1.5,
                  }}>
                    {issue.description}
                  </div>
                )}
              </div>
            );
          })}
        </ScrollHintList>
      </Dialog>

      <NegotiationStrategyDialog
        open={showNegotiationDialog}
        onClose={() => setShowNegotiationDialog(false)}
        leverageMin={negotiationLeverage.min}
        leverageMax={negotiationLeverage.max}
      />

    </div>
  );
};

export default SubscoreDetailCard;
